import React, { useEffect, useRef } from 'react';
import { Box, Button, Modal, ModalClose, ModalDialog, Stack, Typography } from '@mui/joy';
import type { OpenedOrderAcceptance } from './jobCard.service';
import JobCardOaPreview from './JobCardOaPreview';

const OA_MODAL_Z = 1500;

type JobCardOaPreviewModalProps = {
  preview: OpenedOrderAcceptance | null;
  onClose: () => void;
};

export default function JobCardOaPreviewModal({ preview, onClose }: JobCardOaPreviewModalProps) {
  const open = !!preview;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // In the installed app there is no browser chrome, so the phone's back gesture
  // would leave the app. Push a history entry while open so back closes the preview.
  useEffect(() => {
    if (!open) return undefined;
    let poppedByBack = false;
    window.history.pushState({ ...(window.history.state || {}), oaPreview: true }, '');
    const onPop = () => {
      poppedByBack = true;
      onCloseRef.current();
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      if (!poppedByBack && window.history.state?.oaPreview) window.history.back();
    };
  }, [open]);

  return (
    <Modal open={!!preview} onClose={onClose} sx={{ zIndex: OA_MODAL_Z }}>
      <ModalDialog
        layout="fullscreen"
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          width: { md: 'min(960px, 96vw)' },
          height: { xs: '100dvh', md: 'min(92vh, 960px)' },
          maxHeight: { xs: '100dvh' },
          maxWidth: '100%',
          borderRadius: { xs: 0, md: 'md' },
          inset: { md: 'auto' },
          m: { md: 'auto' },
          zIndex: OA_MODAL_Z,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          sx={{
            px: 1.5,
            py: 1,
            // Fullscreen on phones draws under the translucent iOS status bar; keep the close button tappable.
            pt: { xs: 'calc(env(safe-area-inset-top, 0px) + 8px)', md: 1 },
            pl: { xs: 'calc(env(safe-area-inset-left, 0px) + 12px)', md: 1.5 },
            pr: { xs: 'calc(env(safe-area-inset-right, 0px) + 12px)', md: 1.5 },
            borderBottom: '1px solid',
            borderColor: 'divider',
            minHeight: 52,
            flexShrink: 0,
          }}
        >
          <Typography level="title-sm" sx={{ pr: 1, wordBreak: 'break-word', flex: 1 }}>
            {preview?.name || 'Order Acceptance'}
          </Typography>
          {preview && (
            <Button
              size="sm"
              variant="plain"
              component="a"
              href={preview.url}
              target="_blank"
              rel="noopener noreferrer"
              download={preview.name}
              sx={{ minHeight: 40, flexShrink: 0 }}
            >
              Open file
            </Button>
          )}
          <ModalClose onClick={onClose} sx={{ position: 'static', minWidth: 44, minHeight: 44 }} />
        </Stack>
        <Box sx={{ flex: 1, minHeight: 0, bgcolor: 'neutral.softBg', pb: { xs: 'env(safe-area-inset-bottom, 0px)', md: 0 } }}>
          {preview && <JobCardOaPreview preview={preview} />}
        </Box>
      </ModalDialog>
    </Modal>
  );
}
